import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";

import "./DeviceForm.css";
import { BorderColor } from "@mui/icons-material";

interface props {
	device: any;
	fetchData: any;
}

const DeviceForm: React.FC<props> = ({ device, fetchData }) => {
	const [clients, setClients] = useState([]);
	const [sites, setSites] = useState([]);
	const [buildings, setBuildings] = useState([]);
	const [areas, setAreas] = useState([]);
	const [formMsg, setFormMsg] = useState("");
	const [formMsgColor, setFormMsgColor] = useState("");
	const [formMsgVisibility, setFormMsgVisibility] = useState(false);
	const [buildingVisibility, setBuildingVisibility] = useState(true);
	const [areaVisibility, setAreaVisibility] = useState(true);
	const [submitBtnVisibility, setSubmitBtnVisibility] = useState(true);

	const serverUrl = `${process.env.REACT_APP_PROTOCOL}://${process.env.REACT_APP_SERVER_HOST}:${process.env.REACT_APP_SERVER_HOST_PORT}`;

	const fetchEntityData = useCallback(
		(param: string, body: any) => {
			axios
				.post(`${serverUrl}/api/${param}`, body, {
					withCredentials: true,
				})
				.then(async function (response) {
					const apiData = await response.data;

					const setData = () => {
						switch (param) {
							case "clients":
								setClients(apiData.data);
								return;
							case "sites":
								setSites(apiData.data);
								return;
							case "buildings":
								setBuildings(apiData.data);
								return;
							case "areas":
								setAreas(apiData.data);
								return;
						}
					};

					apiData.status === "success"
						? setData()
						: console.log("error fetching data");
				})
				.catch(function (error) {
					console.error(error);
				});
		},
		[serverUrl]
	);

	useEffect(() => {
		fetchEntityData("clients", {});
		fetchEntityData("sites", { clientId: device.client });
		fetchEntityData("buildings", { siteId: device.site });
		fetchEntityData("areas", { buildingId: device.building });
	}, [device.building, device.client, device.site, fetchEntityData]);

	const handleSubmit = (values: any) => {
		const body = {
			name: values.name,
			description: values.description,
			volume: Number(values.volume),
			area: Number(values.area),
			deviceId: Number(values.deviceId),
		};

		axios
			.post(`${serverUrl}/api/device-update`, body, {
				withCredentials: true,
			})
			.then(async function (response) {
				const apiData = await response.data;
				if (apiData.data === "updated" && apiData.status === "success") {
					setFormMsg("Updated");
					setFormMsgColor("#32cd32");
					setFormMsgVisibility(true);
					fetchData();
				} else {
					setFormMsg("Error Updating");
					setFormMsgColor("#ff0000");
					setFormMsgVisibility(true);
				}
			})
			.catch(function (error) {
				console.error(error);
				setFormMsg("Error Updating");
				setFormMsgColor("#ff0000");
				setFormMsgVisibility(true);
			});
	};
	return (
		<div className="form-div">
			<Formik
				initialValues={{
					name: device.name || "",
					description: device.description || "",
					volume: device.volume || "",
					client: device.client || "",
					site: device.site || "",
					building: device.building || "",
					area: device.area || null,
					deviceId: device.deviceId || "",
				}}
				validationSchema={Yup.object({
					name: Yup.string().required("Required"),
					description: Yup.string().max(
						199,
						"Description must be less than 200 characters"
					),
					volume: Yup.number().min(1, "Invalid number, number must be > 0"),
					client: Yup.string().required("Required"),
					site: Yup.string().required("Required"),
					building: Yup.string().required("Required"),
					area: Yup.string().required("Required"),
				})}
				onSubmit={(values) => handleSubmit(values)}
			>
				{({ setFieldValue, values }) => (
					<Form
						className="device-form"
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								e.preventDefault();
							}
						}}
					>
						<label>Device name</label>
						<Field
							name="name"
							type="text"
							placeholder="Enter device name"
						/>
						<p className="input-err">
							<ErrorMessage name="name" />
						</p>

						<label>Description</label>
						<Field
							name="description"
							as="textarea"
							placeholder="Enter device description"
							className="device-form-textarea"
						/>
						<p className="input-err">
							<ErrorMessage name="description" />
						</p>

						<label>Tank Volume (ℓ)</label>
						<Field
							name="volume"
							type="number"
							placeholder="Enter tank volume"
						/>
						<p className="input-err">
							<ErrorMessage name="volume" />
						</p>
						<div>
							<div>
								<label>Client</label>
								<Field
									className="my-select"
									as="select"
									name="client"
									onChange={(e: any) => {
										const selectedValue = e.target.value;
										setFieldValue("client", selectedValue);
										setFieldValue("site", "");
										fetchEntityData("sites", { clientId: selectedValue });
										setFormMsgVisibility(false);
										setBuildingVisibility(false);
										setAreaVisibility(false);
										setSubmitBtnVisibility(false);
									}}
								>
									{clients.map((item: any) => (
										<option
											key={item.id}
											value={item.id}
										>
											{item.name}
										</option>
									))}
								</Field>

								<p className="input-err">
									<ErrorMessage name="client" />
								</p>
							</div>
							<div>
								<label>Site</label>
								<Field
									className="my-select"
									as="select"
									name="site"
									onChange={(e: any) => {
										const selectedValue = e.target.value;
										setFieldValue("site", selectedValue);
										setFieldValue("building", "");
										fetchEntityData("buildings", { siteId: selectedValue });
										setFormMsgVisibility(false);
										setBuildingVisibility(true);
										setAreaVisibility(false);
										setSubmitBtnVisibility(false);
									}}
								>
									<option value=""></option>
									{sites.map((item: any) => (
										<option
											key={item.id}
											value={item.id}
										>
											{item.name}
										</option>
									))}
								</Field>
								<p className="input-err">
									<ErrorMessage name="site" />
								</p>
							</div>
							{buildingVisibility ? (
								<>
									<div>
										<label>Building</label>
										<Field
											className="my-select"
											as="select"
											name="building"
											onChange={(e: any) => {
												const selectedValue = e.target.value;
												setFieldValue("building", selectedValue);
												setFieldValue("area", "");
												fetchEntityData("areas", { buildingId: selectedValue });
												setFormMsgVisibility(false);
												setBuildingVisibility(true);
												setAreaVisibility(true);
												setSubmitBtnVisibility(false);
											}}
										>
											<option value=""></option>
											{buildings.map((item: any) => (
												<option
													key={item.id}
													value={item.id}
												>
													{item.name}
												</option>
											))}
										</Field>
										<p className="input-err">
											<ErrorMessage name="building" />
										</p>
									</div>
								</>
							) : null}
							{areaVisibility ? (
								<>
									<div>
										<label>Area</label>
										<Field
											className="my-select"
											as="select"
											name="area"
											onChange={(e: any) => {
												const selectedValue = e.target.value;
												setFieldValue("area", selectedValue);
												setSubmitBtnVisibility(true);
											}}
										>
											<option value=""></option>
											{areas.map((item: any) => (
												<option
													key={item.id}
													value={item.id}
												>
													{item.name}
												</option>
											))}
										</Field>
										<p className="input-err">
											<ErrorMessage name="area" />
										</p>
									</div>
								</>
							) : null}
							{formMsgVisibility ? (
								<p style={{ color: formMsgColor }}>{formMsg}</p>
							) : null}
						</div>

						{submitBtnVisibility ? <button type="submit">Save</button> : null}
					</Form>
				)}
			</Formik>
		</div>
	);
};

export default DeviceForm;
